import { Link } from '@inertiajs/react';
import type { AdventureAttempt } from '@/types/adventures';

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

interface PaginatedAttempts {
    data: AdventureAttempt[];
    links: PaginationLink[];
    last_page: number;
}

interface AttemptsTabProps {
    adventureAttempts: PaginatedAttempts;
    hexId: number;
    readyToCompleteCount: number;
    onTabChange: (tab: string) => void;
}

/**
 * Get status stamp info for the envelope
 */
function getStatusStampInfo(attempt: AdventureAttempt): {
    text: string;
    subText?: string;
    color: string;
    borderColor: string;
    rotation: number;
} {
    const status = attempt.status.value;

    if (status === 'in_progress' && attempt.is_ready_to_complete) {
        return {
            text: 'READY',
            subText: 'TO COMPLETE',
            color: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgba(22, 163, 74, 0.6)',
            rotation: -8,
        };
    }
    if (status === 'in_progress') {
        return {
            text: 'IN PROGRESS',
            color: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgba(37, 99, 235, 0.6)',
            rotation: -5,
        };
    }
    if (status === 'success') {
        return {
            text: 'SUCCESS',
            color: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgba(22, 163, 74, 0.6)',
            rotation: -12,
        };
    }
    if (status === 'partial') {
        return {
            text: 'PARTIAL',
            subText: 'SUCCESS',
            color: 'rgba(234, 179, 8, 0.8)',
            borderColor: 'rgba(202, 138, 4, 0.6)',
            rotation: -6,
        };
    }
    if (status === 'failure') {
        return {
            text: 'FAILED',
            color: 'rgba(239, 68, 68, 0.8)',
            borderColor: 'rgba(220, 38, 38, 0.6)',
            rotation: -10,
        };
    }
    return {
        text: status.toUpperCase(),
        color: 'rgba(107, 114, 128, 0.8)',
        borderColor: 'rgba(75, 85, 99, 0.6)',
        rotation: -7,
    };
}

/**
 * Get a pseudo-random rotation for envelope based on ID
 */
function getEnvelopeRotation(id: number): number {
    const seed = (id * 11) % 17;
    return (seed - 8) * 0.3;
}

export function AttemptsTab({
    adventureAttempts,
    hexId,
    readyToCompleteCount,
    onTabChange,
}: AttemptsTabProps) {
    return (
        <div className="max-w-4xl mx-auto">
            {/* Header - styled like mail room */}
            <div className="flex items-center justify-between mb-8">
                <div className="text-center flex-1">
                    <h2 className="text-3xl font-serif font-bold text-amber-100 tracking-wide drop-shadow-lg">
                        ✉ Quest Dispatches ✉
                    </h2>
                    <p className="text-amber-200/60 text-sm mt-1 font-serif italic">
                        Reports from your adventuring parties
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    {readyToCompleteCount > 0 && (
                        <span className="px-3 py-1 bg-green-600/20 border border-green-500/50 text-green-300 rounded-full text-sm font-serif">
                            {readyToCompleteCount} awaiting review
                        </span>
                    )}
                    <button
                        onClick={() => onTabChange('adventures')}
                        className="text-amber-400 hover:text-amber-300 transition-colors font-serif"
                    >
                        ← Quest Board
                    </button>
                </div>
            </div>

            {/* Attempts List - styled as envelopes */}
            {adventureAttempts.data.length === 0 ? (
                <div
                    className="rounded-lg p-12 text-center"
                    style={{
                        background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                        border: '4px solid #3d2a1f',
                    }}
                >
                    <p className="text-amber-200/70 font-serif text-lg italic">
                        No dispatches have arrived yet...
                    </p>
                    <button
                        onClick={() => onTabChange('adventures')}
                        className="mt-4 px-6 py-2 bg-amber-700 hover:bg-amber-600 text-amber-100 rounded border-2 border-amber-600 transition-colors font-serif"
                    >
                        Visit the Quest Board
                    </button>
                </div>
            ) : (
                <div
                    className="rounded-lg p-6 relative"
                    style={{
                        background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                        border: '4px solid #3d2a1f',
                    }}
                >
                    {/* Envelopes Stack */}
                    <div className="space-y-6 relative z-10">
                        {adventureAttempts.data.map((attempt) => (
                            <QuestEnvelope
                                key={attempt.id}
                                attempt={attempt}
                                hexId={hexId}
                                rotation={getEnvelopeRotation(attempt.id)}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {adventureAttempts.last_page > 1 && (
                        <div className="flex justify-center gap-2 mt-8 pt-6 border-t border-amber-900/30">
                            {adventureAttempts.links.map((link, idx) => (
                                <span key={idx}>
                                    {link.url ? (
                                        <Link
                                            href={link.url}
                                            className={`px-3 py-1 rounded font-serif ${
                                                link.active
                                                    ? 'bg-amber-700 text-amber-100'
                                                    : 'bg-amber-900/50 text-amber-300 hover:bg-amber-800/50'
                                            }`}
                                            dangerouslySetInnerHTML={{ __html: link.label }}
                                        />
                                    ) : (
                                        <span
                                            className="px-3 py-1 rounded bg-amber-900/30 text-amber-600 font-serif"
                                            dangerouslySetInnerHTML={{ __html: link.label }}
                                        />
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Individual quest envelope
 */
function QuestEnvelope({
    attempt,
    hexId,
    rotation,
}: {
    attempt: AdventureAttempt;
    hexId: number;
    rotation: number;
}) {
    const statusStamp = getStatusStampInfo(attempt);

    return (
        <Link
            href={`/hex/${hexId}?tab=attempt-detail&attemptId=${attempt.id}`}
            className="block relative group"
            style={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) translateY(-4px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = `rotate(${rotation}deg)`;
            }}
        >
            {/* Envelope */}
            <div
                className="relative overflow-hidden rounded-sm"
                style={{
                    background: 'linear-gradient(180deg, #f5edd6 0%, #e8dcc4 30%, #d9cbb0 100%)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(0,0,0,0.1)',
                    border: '1px solid #c9b896',
                }}
            >
                {/* Envelope flap (top triangle) */}
                <div
                    className="absolute top-0 left-0 right-0 h-16 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, #ede4d0 0%, #e0d4be 100%)',
                        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                        borderBottom: '1px solid #c9b896',
                    }}
                />

                {/* Envelope flap shadow */}
                <div
                    className="absolute top-14 left-0 right-0 h-4 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, transparent 100%)',
                    }}
                />

                {/* Postage Stamp - top right */}
                <div
                    className="absolute top-4 right-4 z-10"
                    style={{ transform: 'rotate(2deg)' }}
                >
                    <div
                        className="w-14 h-16 flex items-center justify-center"
                        style={{
                            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 50%, #8B4513 100%)',
                            border: '2px solid #5D3A1A',
                            boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        }}
                    >
                        {/* Stamp perforation effect */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: `
                                radial-gradient(circle at 0 0, transparent 2px, #8B4513 2px) -2px -2px / 6px 6px,
                                radial-gradient(circle at 0 0, transparent 2px, #8B4513 2px) calc(100% + 2px) -2px / 6px 6px,
                                radial-gradient(circle at 0 0, transparent 2px, #8B4513 2px) -2px calc(100% + 2px) / 6px 6px,
                                radial-gradient(circle at 0 0, transparent 2px, #8B4513 2px) calc(100% + 2px) calc(100% + 2px) / 6px 6px
                            `,
                        }} />
                        <div className="text-center">
                            <div className="text-amber-100 text-lg">⚔</div>
                            <div className="text-amber-200/80 text-[8px] font-serif uppercase tracking-wider">
                                Guild
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="pt-20 pb-6 px-8 relative">
                    {/* Decorative seal/emblem in center-left */}
                    <div className="absolute left-6 top-20 opacity-10 pointer-events-none">
                        <div className="text-6xl text-amber-900">⚜</div>
                    </div>

                    {/* Quest Name - Center, like address */}
                    <div className="text-center mb-4 relative z-10">
                        <div className="text-xs font-serif text-amber-800/50 uppercase tracking-widest mb-1">
                            Quest Report
                        </div>
                        <h3 className="text-xl font-serif font-bold text-amber-900 group-hover:text-amber-700 transition-colors">
                            {attempt.adventure?.name || 'Unknown Adventure'}
                        </h3>
                        <div className="h-px w-32 mx-auto mt-2 bg-amber-800/30" />
                    </div>

                    {/* Details Row */}
                    <div className="flex items-center justify-center gap-6 text-sm font-serif text-amber-800/70 mb-4">
                        <span>
                            {attempt.characters?.length || 0} adventurer{(attempt.characters?.length || 0) !== 1 ? 's' : ''}
                        </span>
                        <span className="text-amber-800/30">•</span>
                        <span>
                            {attempt.started_at ? new Date(attempt.started_at).toLocaleDateString() : 'N/A'}
                        </span>
                        {attempt.status.value === 'in_progress' && !attempt.is_ready_to_complete && attempt.success_chance != null && (
                            <>
                                <span className="text-amber-800/30">•</span>
                                <span className="text-amber-700">
                                    {Math.round(attempt.success_chance)}% success chance
                                </span>
                            </>
                        )}
                    </div>

                    {/* Status Ink Stamp - overlaid on envelope */}
                    <div
                        className="absolute bottom-4 right-6"
                        style={{ transform: `rotate(${statusStamp.rotation}deg)` }}
                    >
                        <div
                            className="px-4 py-2 rounded-sm border-4 border-dashed"
                            style={{
                                borderColor: statusStamp.borderColor,
                                background: `linear-gradient(135deg, ${statusStamp.color.replace('0.8', '0.1')} 0%, ${statusStamp.color.replace('0.8', '0.15')} 100%)`,
                            }}
                        >
                            {/* Inner ring */}
                            <div
                                className="absolute inset-1 rounded-sm border-2"
                                style={{ borderColor: statusStamp.borderColor }}
                            />
                            <div className="relative text-center">
                                <div
                                    className="text-sm font-serif font-bold uppercase tracking-wider"
                                    style={{ color: statusStamp.color.replace('0.8', '1') }}
                                >
                                    {statusStamp.text}
                                </div>
                                {statusStamp.subText && (
                                    <div
                                        className="text-xs font-serif uppercase tracking-wider -mt-0.5"
                                        style={{ color: statusStamp.color.replace('0.8', '0.9') }}
                                    >
                                        {statusStamp.subText}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Click hint */}
                    <div className="text-center">
                        <span className="text-xs font-serif text-amber-800/40 italic">
                            Click to open dispatch →
                        </span>
                    </div>
                </div>

                {/* Paper texture */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-20"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.3\'/%3E%3C/svg%3E")',
                    }}
                />
            </div>

            {/* Hover lift shadow */}
            <div
                className="absolute inset-0 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -z-10"
                style={{
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    transform: 'translateY(4px)',
                }}
            />
        </Link>
    );
}
